'use client'

import {
  Button as MuiButton,
  type ButtonOwnProps as MuiButtonOwnProps,
  type PolymorphicProps,
} from '@mui/base'
import { clsx } from 'clsx'
import {
  type ComponentProps,
  type ElementType,
  type JSX,
  type ReactElement,
  useEffect,
} from 'react'

export type ButtonOwnProps = Omit<
  MuiButtonOwnProps,
  'href' | 'rootElementName' | 'to' | 'type'
> & {
  /**
   * The color of the component
   * @default primary
   */
  color?: 'primary' | 'secondary'

  /**
   * Icon placed after the children
   */
  endIcon?: ReactElement<ComponentProps<'svg'>, 'svg'> | false | null

  href?: string

  /**
   * The size of the component.
   * @default large
   */
  size?: 'large' | 'medium' | 'small'

  /**
   * Icon placed before the children
   */
  startIcon?: ReactElement<ComponentProps<'svg'>, 'svg'> | false | null

  /**
   * The variant to use.
   * @default filled
   */
  variant?: 'filled' | 'text'
}

export interface ButtonTypeMap<
  RootComponentType extends ElementType = 'button',
> {
  defaultComponent: RootComponentType
  props: ButtonOwnProps
}

export type ButtonProps<
  RootComponentType extends ElementType = ButtonTypeMap['defaultComponent'],
> = PolymorphicProps<ButtonTypeMap<RootComponentType>, RootComponentType>

/**
 * Buttons allow users to take actions and make choices, with a single tap
 */
export const Button = <RootComponentType extends ElementType = 'button'>(
  props: ButtonProps<RootComponentType>,
): JSX.Element => {
  const {
    children,
    className,
    color: colorProp,
    endIcon,
    size = 'large',
    startIcon,
    variant = 'filled',
    ...other
  } = props

  const color = colorProp ?? 'primary'

  useEffect(() => {
    if (
      colorProp &&
      variant !== 'filled' &&
      process.env.NODE_ENV !== 'production'
    ) {
      console.warn(
        `Color property could be applied only to variant "filled" and not to "${variant}"`,
      )
    }
  }, [colorProp, variant])

  useEffect(() => {
    if (
      !endIcon &&
      !startIcon &&
      variant === 'text' &&
      process.env.NODE_ENV !== 'production'
    ) {
      console.warn(
        'Variant "text" should have an icon, otherwise might be a regular link',
      )
    }
  }, [endIcon, startIcon, variant])

  return (
    <MuiButton
      className={clsx(
        'button-deprecated',
        variant === 'filled' &&
          color === 'primary' &&
          'button-deprecated_primary',
        variant === 'filled' &&
          color === 'secondary' &&
          'button-deprecated_secondary',
        variant === 'filled' && 'button-deprecated_filled',
        size === 'large' && 'button-deprecated_large',
        size === 'medium' && 'button-deprecated_medium',
        size === 'small' && 'button-deprecated_small',
        variant === 'text' && 'button-deprecated_text',
        className,
      )}
      {...other}
    >
      {startIcon ? (
        <span className="button-deprecated__icon button-deprecated__icon_start">
          {startIcon}
        </span>
      ) : null}
      {children}
      {endIcon ? (
        <span className="button-deprecated__icon button-deprecated__icon_end">
          {endIcon}
        </span>
      ) : null}
    </MuiButton>
  )
}
